import React, { useEffect, useState } from 'react';
import { laneApi, TeamMember, LaneId } from '../../api/laneApi';
import { Button } from '../../components/ui';
import './lanes.css';

/**
 * Standalone "assign to a staff member" modal (staff-assignment
 * restoration fast-follow), mirroring `ClaimPickerModal`'s shape: used for
 * every "pick one team member and confirm" flow -- bulk-assign in the
 * Client/Lender/Post/Manual lane bulk bars, and the single-task Assign/
 * Reassign action in `TaskDetailPane`.
 *
 * Unlike claims (searched via `ClaimPicker`'s async typeahead), the team
 * roster is a short, fixed list (see app/routes/team_tasks.py's
 * TEAM_MEMBERS) -- fetched once via `laneApi.getTeamMembers()` and rendered
 * as a plain `<select>`, with an explicit "Unassign" option threading
 * through as `null` (the legacy Tasks.tsx assign menu never had an
 * unassign action at all -- this is new, not a like-for-like port).
 *
 * Staff-assignment fast-follow #1 (incident email: "I don't know how many
 * tasks I am assigning to each person") -- each option now shows the
 * member's live pending workload ("Erin — 143 pending"), sourced from
 * `TeamMember.pendingCount` on the same roster fetch (GET
 * /ui/team-tasks/team-members, extended server-side). Refetched every time
 * this component mounts, which is every time a caller opens it: all five
 * call sites (ClientLane x2, LenderLane, ManualLane, TaskDetailPane) close
 * the modal synchronously as part of confirming (or, TaskDetailPane's
 * single-task flow, on the success path only -- an error leaves it open
 * with counts still accurate, since nothing was assigned) and none reopen
 * it without a full unmount/remount, so there is no "stale counts in a
 * modal left open after a successful assign" case to additionally guard.
 * `onConfirm`'s second argument threads the selected option's own
 * pendingCount (or the roster's unassignedCount when unassigning) back to
 * the caller, for confirm-copy that wants to echo it (see
 * ClientLane.confirmAssignMemberAll).
 */

const UNASSIGNED_VALUE = '__unassign__';

export interface MemberPickerModalProps {
  title: string;
  hint?: React.ReactNode;
  confirmLabel?: string;
  /** Preselects the currently-assigned member (detail pane's reassign flow). */
  currentAssignee?: string | null;
  /** Scopes each option's `pendingCount` to this lane (reviewer blocker:
   * without it, `laneApi.getTeamMembers()` returned a member's pending
   * workload summed across all four lanes, while every other lane surface
   * -- the list itself, the tab count badge -- is scoped to one lane).
   * Every caller opens this modal from a single lane's context, so this is
   * always supplied in practice; left optional only so any not-yet-updated
   * caller keeps the old cross-lane total rather than a compile error. */
  lane?: LaneId;
  /** `pendingCount` is the selected member's live pending-task count at
   * confirm time (0 when unassigning) -- lets callers echo it in their own
   * confirm copy without re-fetching the roster themselves. */
  onConfirm: (member: string | null, pendingCount: number) => void | Promise<void>;
  onClose: () => void;
  /** Disables the confirm button while a parent-owned action is in flight. */
  processing?: boolean;
}

export const MemberPickerModal: React.FC<MemberPickerModalProps> = ({
  title, hint, confirmLabel = 'Assign', currentAssignee, lane, onConfirm, onClose, processing,
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>(currentAssignee || '');

  useEffect(() => {
    laneApi.getTeamMembers(lane)
      .then(setMembers)
      .catch(() => setError('Failed to load team members.'))
      .finally(() => setLoading(false));
  }, [lane]);

  const selectedMember = members.find((m) => m.name === selected);

  const handleConfirm = () => {
    if (!selected) return;
    if (selected === UNASSIGNED_VALUE) {
      onConfirm(null, 0);
      return;
    }
    onConfirm(selected, selectedMember?.pendingCount ?? 0);
  };

  return (
    <div className="lane-modal-backdrop" onClick={onClose}>
      <div className="lane-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="lane-modal__title">{title}</h2>
        {hint && <p className="lane-modal__hint">{hint}</p>}
        {loading && <p className="lane-modal__hint">Loading team members…</p>}
        {error && <div className="lane-modal__error">{error}</div>}
        {!loading && !error && (
          <select
            className="lane-modal__input"
            aria-label="Team member"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            autoFocus
          >
            <option value="" disabled>Select a team member…</option>
            {members.map((m) => (
              <option key={m.email} value={m.name}>{`${m.name} — ${m.pendingCount ?? 0} pending`}</option>
            ))}
            <option value={UNASSIGNED_VALUE}>Unassign</option>
          </select>
        )}
        {selectedMember && (
          <p className="lane-modal__hint">
            {`${selectedMember.name} currently has ${selectedMember.pendingCount ?? 0} pending task${(selectedMember.pendingCount ?? 0) === 1 ? '' : 's'}.`}
          </p>
        )}
        <div className="lane-modal__actions">
          <Button variant="secondary" onClick={onClose} disabled={processing}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!selected || processing}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MemberPickerModal;
